import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, unwrap } from '@/shared/api-client/workspace';

/**
 * The PosDevice aggregate (ADR 0044): a logical selling station per Venue, distinct
 * from the PosInstallation (physical tablet) resource in `../pos-devices`. Mounted at
 * `/pos-stations` — see `PosDeviceController`'s javadoc for why it isn't `/pos-devices`.
 */
export interface PosDevice {
  id: string;
  venueId: string;
  name: string;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PosDeviceList {
  items: PosDevice[];
}

const listKey = (venueId: string, includeArchived: boolean) =>
  ['venues', venueId, 'pos-stations', includeArchived] as const;

export const getPosStations = (venueId: string, includeArchived = false): Promise<PosDeviceList> =>
  unwrap(
    apiClient.GET('/api/v1/venue-workspace/venues/{venueId}/pos-stations', {
      params: { path: { venueId }, query: { includeArchived } },
    }),
  ) as Promise<PosDeviceList>;

export const createPosStation = (venueId: string, name: string): Promise<PosDevice> =>
  unwrap(
    apiClient.POST('/api/v1/venue-workspace/venues/{venueId}/pos-stations', {
      params: { path: { venueId } },
      body: { name },
    }),
  ) as Promise<PosDevice>;

export const renamePosStation = (
  venueId: string,
  deviceId: string,
  name: string,
): Promise<PosDevice> =>
  unwrap(
    apiClient.PATCH('/api/v1/venue-workspace/venues/{venueId}/pos-stations/{deviceId}', {
      params: { path: { venueId, deviceId } },
      body: { name },
    }),
  ) as Promise<PosDevice>;

export const setPosStationArchived = (
  venueId: string,
  deviceId: string,
  archived: boolean,
): Promise<PosDevice> =>
  unwrap(
    apiClient.PATCH('/api/v1/venue-workspace/venues/{venueId}/pos-stations/{deviceId}/archived', {
      params: { path: { venueId, deviceId } },
      body: { archived },
    }),
  ) as Promise<PosDevice>;

export function usePosStations(venueId: string, includeArchived = false) {
  return useQuery({
    queryKey: listKey(venueId, includeArchived),
    queryFn: () => getPosStations(venueId, includeArchived),
    enabled: !!venueId,
  });
}

function useInvalidatePosStations(venueId: string) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['venues', venueId, 'pos-stations'] });
}

export function useCreatePosStation(venueId: string) {
  const invalidate = useInvalidatePosStations(venueId);
  return useMutation({
    mutationFn: (name: string) => createPosStation(venueId, name),
    onSuccess: invalidate,
  });
}

export function useRenamePosStation(venueId: string) {
  const invalidate = useInvalidatePosStations(venueId);
  return useMutation({
    mutationFn: ({ deviceId, name }: { deviceId: string; name: string }) =>
      renamePosStation(venueId, deviceId, name),
    onSuccess: invalidate,
  });
}

export function useSetPosStationArchived(venueId: string) {
  const invalidate = useInvalidatePosStations(venueId);
  return useMutation({
    mutationFn: ({ deviceId, archived }: { deviceId: string; archived: boolean }) =>
      setPosStationArchived(venueId, deviceId, archived),
    onSuccess: invalidate,
  });
}
